import React, { useCallback, useContext, useMemo } from 'react';
import { Skeleton } from '@patternfly/react-core';
import _ from 'lodash';
import PropTypes from 'prop-types';

import Icons, { Size } from 'components/Common/Icons';
import Tabs from 'components/Tabs/Tabs';
import { useTranslation } from 'localization/useTranslation';
import AssetDetails from 'components/Asset/AssetPage/AssetDetails';
import dashboardTabs from './dashboardTabs';
import { throttleDate } from 'services/datetime';
import { useProperties } from 'utils/hooks/useProperties';
import AlertContext from 'services/AlertContext';

const AssetView = ({ assetId, dashboards }) => {
  const T = useTranslation();
  const activeTab = Number(localStorage.getItem(`selectedAssetTab-${assetId}`)) ?? 0;

  const { addSuccessAlert, addErrorAlert } = useContext(AlertContext);

  const { asset, isUpdatingProps, updateProps, refetchReported, isLoading, assetModel } = useProperties({ assetId, addSuccessAlert, addErrorAlert });

  const onSelectCallback = useCallback((index) => {
    localStorage.setItem(`selectedAssetTab-${assetId}`, index);
  }, [assetId]);
  // Throttle dashboard date so network request can cache on same time.
  const dashboardDate = useMemo(() => throttleDate(1000), []);


  const tabs = useMemo(() => {
    if (_.isEmpty(asset) || _.isEmpty(assetModel)) {
      return [];
    }

    return [
      ...dashboardTabs(dashboards, dashboardDate),
      {
        label: T.asset.details,
        icon: <Icons.Asset size={Size.SMALL} />,
        component: AssetDetails,
        props: {
          asset,
          assetModel,
          updateProps,
          refetchReported,
          isUpdatingProps
        }
      },
      ..._.map(assetModel.modules || [], (component) => ({
        label: component.displayName,
        component: AssetDetails,
        icon: <Icons.Component size={Size.SMALL} />,
        props: {
          asset,
          assetModel: assetModel,
          componentModel: component,
          updateProps,
          isUpdatingProps,
          refetchReported
        }
      }))
    ];
  }, [asset, assetModel, dashboards, dashboardDate, T.asset.details, updateProps, refetchReported, isUpdatingProps]);

  if (isLoading) {
    return (
      <Skeleton width="95%" height="35px" screenreaderText="Loading contents" />
    );
  }
  if (!asset || !assetModel) {
    return <></>;
  }

  return (
    <React.Fragment>
      <div className="tabs-wrapper">
        <Tabs
          key={'tabs-asset-view-' + assetId}
          tabClassName={'tabs-content'}
          tabs={tabs}
          onSelectCallback={onSelectCallback}
          initialTab={activeTab} />
      </div>
    </React.Fragment>
  );
};

AssetView.propTypes = {
  assetId: PropTypes.string
};

export default AssetView;
